// BOORO: choosing an agent by face and name.
import type { Bot } from "@rakazo/contracts";
import {
  BotAvatar,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@rakazo/ui-web";

/** An agent to choose; the hint says where it works when there is more than one place. */
export type AgentChoice = Pick<Bot, "id" | "name" | "color"> & { hint?: string };

function AgentOption({ bot }: { bot: AgentChoice }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <BotAvatar color={bot.color} identity={bot.id} size={20} />
      <span className="truncate" dir="auto">
        {bot.name}
      </span>
      {bot.hint ? (
        <span className="truncate text-xs text-muted-foreground" dir="auto">
          {bot.hint}
        </span>
      ) : null}
    </span>
  );
}

export function AgentPicker({
  bots,
  value,
  onChange,
  "aria-label": ariaLabel,
  className,
}: {
  bots: readonly AgentChoice[];
  value: string;
  onChange: (botId: string) => void;
  "aria-label": string;
  className?: string;
}) {
  return (
    <Select
      value={value}
      onValueChange={(next) => {
        if (typeof next === "string" && bots.some((bot) => bot.id === next)) onChange(next);
      }}
    >
      <SelectTrigger aria-label={ariaLabel} data-testid="agent-picker" className={className}>
        <SelectValue>
          {(selected: string) => {
            const bot = bots.find((candidate) => candidate.id === selected);
            return bot ? <AgentOption bot={bot} /> : null;
          }}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {bots.map((bot) => (
          <SelectItem key={bot.id} value={bot.id}>
            <AgentOption bot={bot} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
